<template>
  <div class="stats-row">
    <icon-label
        v-if="bloodPressure"
        :item-text="`${bloodPressure} ${bloodPresureUnit}`"
        svgIcon="icon-blood-pressure"
        :date="bloodPressureDate"
        :customClass="bloodPressureColorClass"
        :name="$ucfirst($t('educationOverview.function.keyword.bloodPressureSearch', { catalog : 'content'}))"
        @click="goTo('PatientRecordProgressStatusParameters', 'blood_pressure')"
    ></icon-label>
    <icon-label
        v-if="weight"
        :item-text="`${weight + ' ' + kilogramUnit.unit}`"
        svgIcon="icon-weight"
        :date="weightDate"
        :customClass="BMIColorClass"
        :name="$ucfirst($t('parameters.weight', { catalog : 'content'}))"
        @click="goTo('PatientRecordProgressStatusParameters', 'weight')"
    ></icon-label>
    <icon-label
        v-if="bmi"
        :item-text="`${bmi + ' ' + kilogramSquareMeterUnit.unit}`"
        svgIcon="icon-bmi"
        :date="bmiDate"
        :customClass="BMIColorClass"
        :name="$ucfirst($t('parameters.bmi'))"
        @click="goTo('PatientRecordProgressStatusParameters', 'weight')"
    ></icon-label>
    <icon-label
        v-if="cholesterolLDL"
        :item-text="`LDL: ${cholesterolLDL + ' ' + cholesterolLDLUnit}`"
        svgIcon="icon-cholesterol-ldl"
        :date="cholesterolDate"
        :customClass="cholesterolColorClass"
        :name="$ucfirst($t('parameters.cholesterolldl'))"
        @click="goTo('PatientRecordProgressStatusParameters', 'lipids')"
    ></icon-label>
    <icon-label
        v-if="glucose"
        :item-text="`${glucose + ' ' + glucoseUnit}`"
        svgIcon="icon-glucose"
        :date="diabetesDate"
        :customClass="glucoseColorClass"
        :name="$ucfirst($t('general.hba1cGlucose', 'app_mobile'))"
        @click="goTo('PatientRecordProgressStatusParameters', 'glucose')"
    ></icon-label>
    <icon-label
        v-if="medicationAdherence"
        :item-text="medicationAdherence"
        icon="mdi-pill"
        :date="medicationAdherenceDate"
        :customClass="medicationColorClass"
        :name="$ucfirst($t('medication.medicationAdherence'))"
        @click="goToModule('PatientRecordMedicationProgress', 'MEDICATIONS')"
    ></icon-label>
    <icon-label
        v-if="startMoving"
        :item-text="startMoving"
        icon="mdi-run"
        :date="startMovingDate"
        :customClass="startMovingColorClass"
        :name="$ucfirst($t('screens.startMovingSettingsScreen', { catalog: 'app_mobile' }))"
        @click="goToModule('PatientRecordPAProgress', 'STARTMOVING')"
    ></icon-label>
    <icon-label
        v-if="healthyNutrition"
        :item-text="healthyNutrition"
        icon="mdi-silverware-fork-knife"
        :date="healthyNutritionDate"
        :customClass="healthyNutritionColorClass"
        :name="$ucfirst($t('screens.healthyNutritionSettingsScreen', { catalog: 'app_mobile' }))"
        @click="goToModule('PatientRecordHN', 'HEALTHYNUTRITION')"
    ></icon-label>
    <icon-label
        v-if="smokeCessation"
        :item-text="smokeCessation"
        icon="mdi-smoking-off"
        :date="smokeCessationDate"
        :customClass="smokeCessationColorClass"
        :name="$ucfirst($t('behaviourComponentJourney.SMOKECESSATION'))"
        @click="goToModule('PatientRecordSmokingProgress', 'SMOKECESSATION')"
    ></icon-label>
    <icon-label
        v-if="stressManagement"
        :item-text="stressManagement"
        icon="mdi-meditation"
        :date="stressManagementDate"
        :customClass="stressManagementColorClass"
        :name="$ucfirst($t('screens.stressReliefSettingsScreen', { catalog: 'app_mobile' }))"
        :exclamation="stressManagementExclamation"
        @click="goToModule('PatientRecordStress', 'STRESSMANAGEMENT')"
    ></icon-label>
    <icon-label
        v-if="knowledgeLevelName"
        :item-text="knowledgeLevelName.toString()"
        :imgIcon="knowledgeLevelIcon"
        :customClass="knowledgeColorClass"
        :name="$ucfirst($t('behaviourChangeGoals.knowledgelevel'))"
        :date="knowledgeLevelDate"
        @click="goTo('PatientRecordKnowledge')"
    ></icon-label>
  </div>
</template>

<script>
import IconLabel from './IconLabel';
import Inventory from 'dashboard/store/patient-summary/inventory';
import {UCUMUnit} from "../../../../helper/UCUMUnit";
import {mapGetters} from 'vuex';

/**
 * Component StatsRow.vue
 *
 * @module StatsRow
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.3] app/src/APP_DASHBOARD/templates/components/Parts/StatsRow.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'StatsRow',
  data() {
    return {
      parameterClass: {
        red: 'stats-row--red',
        orange: 'stats-row--orange',
        green: 'stats-row--green',
      }
    };
  },
  components: {
    IconLabel,
  },
  props: {
    bloodPressure: {
      type: String,
      required: false,
    },
    weight: {
      type: [String, Number],
      required: false,
    },
    cholesterolLDL: {
      type: [String, Number],
      required: false,
    },
    cholesterolLDLUnit: {
      type: String,
      required: false,
      default: () => ""
    },
    glucose: {
      type: [String, Number],
      required: false
    },
    glucoseUnit: {
      type: String,
      required: false,
      default: () => ""
    },
    knowledgeLevelName: {
      type: String,
      required: false,
    },
    behaviourGoals: {
      type: Object,
      default: () => new Inventory(),
    },
    outcomeGoals: {
      type: Object,
      default: () => new Inventory(),
    },
    bmi: {
      type: [String, Number],
      required: false,
    },
    bmiDate: {
      type: String,
      required: false,
    },
  },
  computed: {
    ...mapGetters({
      isModuleEnabled: "enabledModules/getIsModuleEnabled",
      cholesterolLDLInMgDl: 'patientSummary/cholesterolLDL',
      knowledgeLevelKey: 'patientSummary/knowledgeLevelKey',
      hadEvent: 'patientSummary/hadSecondVascularEventOnStatins',
    }),
    kilogramUnit: {
      get() {
        return UCUMUnit.KILOGRAM;
      }
    },
    kilogramSquareMeterUnit: {
      get() {
        return UCUMUnit.KILOGRAM_PER_SQUARE_METER;
      }
    },
    mgDlUnit: {
      get() {
        return UCUMUnit.MILLIGRAM_PER_DECILITER;
      }
    },
    bloodPresureUnit: {
      get() {
        return UCUMUnit.MILLIMETER_OF_MERCURY.translate(this.$t);
      }
    },
    medicationAdherence() {
      return this.behaviourGoals.get('MEDICATIONS').label;
    },
    startMoving() {
      return this.behaviourGoals.get('STARTMOVING').label;
    },
    healthyNutrition() {
      return this.behaviourGoals.get('HEALTHYNUTRITION').label;
    },
    stressManagement() {
      return this.behaviourGoals.get('STRESSMANAGEMENT').label;
    },
    smokeCessation() {
      let smokingBehaviour = this.behaviourGoals.get('SMOKECESSATION');

      if (smokingBehaviour.value == 'active_smoker') {

        return this.$ucfirst(this.$t('smokingRiskProfile.activeSmoker',
            {
              params: {
                dependence: this.$t(smokingBehaviour.extraInformation.dependence)
              }
            }
        ));
      } else {
        return this.behaviourGoals.get('SMOKECESSATION').label
      }
    },
    bloodPressureDate() {
      return this.outcomeGoals.get("LOWERING_BLOOD_PRESSURE").date;
    },
    bloodPressureColorClass() {
      const bloodPressure = parseInt(this.bloodPressure.split('/')[0]);

      if (bloodPressure > 160 || bloodPressure < 80) {
        return this.parameterClass.red;
      } else if (bloodPressure >= 130 || bloodPressure < 100) {
        return this.parameterClass.orange;
      } else {
        return this.parameterClass.green
      }
    },
    weightDate() {
      return this.outcomeGoals.get("HEALTHY_WEIGHT").date;
    },
    BMIColorClass() {
      if (this.bmi > 30 || this.bmi < 15) {
        return this.parameterClass.red;
      } else if (this.bmi > 20 && this.bmi <= 25) {
        return this.parameterClass.green;
      } else {
        return this.parameterClass.orange;
      }
    },
    cholesterolDate() {
      return this.outcomeGoals.get("LOWERING_CHOLESTEROL").date;
    },
    cholesterolColorClass() {
      const cholesterolLDL = parseFloat(this.cholesterolLDLInMgDl);
      const minValue = this.hadEvent ? 1 : 1.4;
      const maxValue = this.hadEvent ? 1.4 : 1.8;

      // Convert from mg/dl to mmol/l - divide by 38.67
      const cholesterolLDLConverted = cholesterolLDL / 38.67;

      if (cholesterolLDLConverted < minValue) {
        return this.parameterClass.green;
      } else if (cholesterolLDLConverted > maxValue) {
        return this.parameterClass.red;
      } else {
        return this.parameterClass.orange;
      }
    },
    diabetesDate() {
      return this.outcomeGoals.get("DIABETES_MANAGEMENT").date;
    },
    glucoseColorClass() {
      const glucose = parseFloat(this.glucose);

      if (glucose >= 6.5) {
        return this.parameterClass.red;
      } else if (glucose >= 6) {
        return this.parameterClass.orange;
      } else {
        return this.parameterClass.green;
      }
    },
    medicationAdherenceDate() {
      return this.behaviourGoals.get('MEDICATIONS').date;
    },
    medicationColorClass() {
      return this.getClassForBehaviourGoal(this.behaviourGoals.get('MEDICATIONS'));
    },
    startMovingDate() {
      return this.behaviourGoals.get('STARTMOVING').date;
    },
    startMovingColorClass() {
      return this.getClassForBehaviourGoal(this.behaviourGoals.get('STARTMOVING'));
    },
    healthyNutritionDate() {
      return this.behaviourGoals.get('HEALTHYNUTRITION').date;
    },
    healthyNutritionColorClass() {
      return this.getClassForBehaviourGoal(this.behaviourGoals.get('HEALTHYNUTRITION'));
    },
    stressManagementDate() {
      return this.behaviourGoals.get('STRESSMANAGEMENT').date;
    },
    stressManagementColorClass() {
      return this.getClassForBehaviourGoal(this.behaviourGoals.get('STRESSMANAGEMENT'));
    },
    smokeCessationDate() {
      return this.behaviourGoals.get('SMOKECESSATION').date;
    },
    smokeCessationColorClass() {
      return this.getClassForBehaviourGoal(this.behaviourGoals.get('SMOKECESSATION'));
    },
    knowledgeLevelDate() {
      return this.behaviourGoals.get('KNOWLEDGE').date;
    },
    stressManagementExclamation() {
      return this.behaviourGoals.get('STRESSMANAGEMENT')?.extraInformation?.exclamation;
    },
    knowledgeLevelIcon() {
      const level = this.behaviourGoals.get('KNOWLEDGE').value;
      switch (level) {
        case 'beginner':
          return this.$asset('image/beginner.png');
        case 'advanced':
          return this.$asset('image/advanced.png');
        case 'expert':
          return this.$asset('image/expert.png');
        default:
          return this.$asset('image/beginner.png');
      }
    },
    knowledgeColorClass() {
      let knowledgeClass = this.getClassForBehaviourGoal(this.behaviourGoals.get('KNOWLEDGE'));

      if (!knowledgeClass && this.knowledgeLevelKey === 'beginner') {
        return this.parameterClass.red;
      }
      return knowledgeClass
    }
  },
  methods: {
    goTo(name, param) {
      if (!param) {
        this.$router.push({name})
      } else {
        // Emit route change in order to prevent duplicated routes by adding a query parameter
        this.$router.push({
          name: name,
          query: {p: param},
          params: {p: param}
        })
      }
    },
    goToModule(name, moduleName) {
      if (this.isModuleEnabled(moduleName)) {
        this.$router.push({name})
      }
    },
    getClassForBehaviourGoal(behaviourGoal) {
      if (!behaviourGoal || !behaviourGoal.color) return '';
      return this.parameterClass[behaviourGoal.color];
    }
  }
};
</script>
